import { useState } from "react";
import { motion } from "framer-motion";
import { Search, Package, CheckCircle2, Truck, Home, XCircle } from "lucide-react";
import { trackOrder, inr, imgSrc } from "@/api";
import { FadeIn, LotusMark } from "@/components/Reveal";

const STEPS = [
  { key: "placed", label: "Placed", icon: Package },
  { key: "confirmed", label: "Confirmed", icon: CheckCircle2 },
  { key: "dispatched", label: "Dispatched", icon: Truck },
  { key: "delivered", label: "Delivered", icon: Home },
];

const TrackOrder = () => {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    setError("");
    setOrder(null);
    setLoading(true);
    try {
      setOrder(await trackOrder(orderNumber, email));
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? "No order found for that number and email — check both and try again."
          : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const activeStep = STEPS.findIndex((s) => s.key === order?.status);
  const cancelled = order?.status === "cancelled";

  return (
    <div data-testid="track-order-page" className="pt-32 pb-24 px-6 min-h-screen">
      <FadeIn className="max-w-xl mx-auto">
        <div className="text-center">
          <LotusMark className="w-9 h-9 text-gold mx-auto" />
          <h1 className="mt-5 font-display text-4xl sm:text-5xl text-maroon-deep">Where Is My Order?</h1>
          <p className="mt-3 text-clay">Your order number (VYR-······) and the email you ordered with.</p>
        </div>

        <form onSubmit={handleTrack} className="mt-10 bg-ivory-card border border-gold/30 p-8 space-y-6">
          <label className="block">
            <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Order Number</span>
            <input
              data-testid="track-order-number-input"
              required
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder="VYR-880659"
              className="mt-2 w-full bg-ivory border border-maroon/20 px-4 py-3.5 text-sm tracking-[0.15em] text-maroon-deep placeholder:text-clay/50 focus:outline-none focus:border-gold transition-colors"
            />
          </label>
          <label className="block">
            <span className="text-[10px] tracking-[0.3em] uppercase text-clay">Email</span>
            <input
              data-testid="track-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-2 w-full bg-ivory border border-maroon/20 px-4 py-3.5 text-sm text-maroon-deep placeholder:text-clay/50 focus:outline-none focus:border-gold transition-colors"
            />
          </label>
          {error && (
            <p data-testid="track-error" className="text-sm text-maroon bg-maroon/5 border border-maroon/20 px-4 py-3">{error}</p>
          )}
          <button
            type="submit"
            data-testid="track-submit-button"
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-maroon text-ivory py-4 text-xs tracking-[0.35em] uppercase hover:bg-maroon-hover transition-colors duration-300 disabled:opacity-60"
          >
            <Search className="w-4 h-4" /> {loading ? "Searching the ledger…" : "Trace My Order"}
          </button>
        </form>

        {order && (
          <motion.div
            data-testid="track-result"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 bg-ivory-card border border-gold/30 p-8"
          >
            <div className="flex items-baseline justify-between">
              <p data-testid="track-result-order-number" className="font-brand tracking-[0.2em] text-lg text-maroon-deep">{order.order_number}</p>
              <p className="text-xs text-clay">
                {new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
            <p className="mt-1 text-sm text-clay">
              {order.customer_name} · {order.city} · {order.payment_method === "cod" ? "Cash on Delivery" : order.payment_status === "paid" ? "Paid Online" : "Payment Pending"}
            </p>

            {cancelled ? (
              <div data-testid="track-cancelled" className="mt-8 flex items-center gap-3 text-maroon">
                <XCircle className="w-5 h-5" />
                <p className="font-display italic text-xl">This order was cancelled.</p>
              </div>
            ) : (
              <div data-testid="track-timeline" className="mt-8 flex items-center">
                {STEPS.map((s, i) => {
                  const reached = activeStep >= i;
                  return (
                    <div key={s.key} className="flex items-center flex-1 last:flex-none">
                      <div className="flex flex-col items-center gap-2">
                        <span
                          data-testid={`track-step-${s.key}`}
                          className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${
                            reached ? "bg-maroon border-maroon text-ivory" : "border-maroon/25 text-clay/50"
                          }`}
                        >
                          <s.icon className="w-4 h-4" />
                        </span>
                        <span className={`text-[9px] tracking-[0.2em] uppercase ${reached ? "text-maroon-deep" : "text-clay/50"}`}>
                          {s.label}
                        </span>
                      </div>
                      {i < STEPS.length - 1 && (
                        <div className={`flex-1 h-px mx-2 mb-5 ${activeStep > i ? "bg-maroon" : "bg-maroon/20"}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <ul className="mt-8 pt-6 border-t border-gold/25 space-y-3">
              {order.items.map((i, idx) => (
                <li key={idx} className="flex items-center gap-4">
                  <div className="w-12 h-14 overflow-hidden ring-1 ring-gold/30 shrink-0">
                    <img src={imgSrc(i.image)} alt={i.name} className="w-full h-full object-cover" />
                  </div>
                  <span className="flex-1 text-sm text-maroon-deep">
                    {i.name} <span className="text-clay/70">· {i.size} × {i.qty}</span>
                  </span>
                  <span className="font-display text-maroon-deep">{inr(i.price * i.qty)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-4 border-t border-gold/25 flex justify-between items-baseline">
              <span className="text-xs tracking-[0.3em] uppercase text-clay">Total</span>
              <span data-testid="track-result-total" className="font-display text-2xl text-maroon-deep">{inr(order.total)}</span>
            </div>
          </motion.div>
        )}
      </FadeIn>
    </div>
  );
};

export default TrackOrder;
